/* @flow */
import * as React from "react";
import { Motion, spring } from "react-motion";
import { scaleLinear } from "d3-scale";
import discountinuousScale from "./discontinuousScale";
import memoize from "lodash/memoize";
import * as Types from "./NextGenChartTypes";

import { bisector, tickStep } from "d3-array";
import {
  timeYear as year,
  timeMonth as month,
  timeWeek as week,
  timeDay as day,
  timeHour as hour,
  timeMinute as minute,
  timeSecond as second,
  timeMillisecond as millisecond
} from "d3-time";

type ScaleXProps = {
  cursor: Types.Cursor,
  timeSpan: number,
  width: number,
  checkpoints: Array<Types.Checkpoint>
};

type ScaleYProps = {
  extent: [number, number],
  height: number,
  modifier: number
};

type Scale = (number => number) & {
  invert: number => number
};

// d3 ticks bullshit
// https://github.com/d3/d3-scale/blob/master/src/time.js

var durationSecond = 1000,
  durationMinute = durationSecond * 60,
  durationHour = durationMinute * 60,
  durationDay = durationHour * 24,
  durationWeek = durationDay * 7,
  durationMonth = durationDay * 30,
  durationYear = durationDay * 365;

// prettier-ignore
var tickIntervals = [
    [second,  1,      durationSecond],
    [second,  5,  5 * durationSecond],
    [second, 15, 15 * durationSecond],
    [second, 30, 30 * durationSecond],
    [minute,  1,      durationMinute],
    [minute,  5,  5 * durationMinute],
    [minute, 15, 15 * durationMinute],
    [minute, 30, 30 * durationMinute],
    [  hour,  1,      durationHour  ],
    [  hour,  3,  3 * durationHour  ],
    [  hour,  6,  6 * durationHour  ],
    [  hour, 12, 12 * durationHour  ],
    [   day,  1,      durationDay   ],
    [   day,  2,  2 * durationDay   ],
    [  week,  1,      durationWeek  ],
    [ month,  1,      durationMonth ],
    [ month,  3,  3 * durationMonth ],
    [  year,  1,      durationYear  ]
  ];

function tickInterval(interval, start, stop, step) {
  if (interval == null) interval = 10;

  // If a desired tick count is specified, pick a reasonable tick interval
  // based on the extent of the domain and a rough estimate of tick size.
  // Otherwise, assume interval is already a time interval and use it.
  if (typeof interval === "number") {
    var target = Math.abs(stop - start) / interval,
      i = bisector(function(i) {
        return i[2];
      }).right(tickIntervals, target);
    if (i === tickIntervals.length) {
      step = tickStep(start / durationYear, stop / durationYear, interval);
      interval = year;
    } else if (i) {
      i =
        tickIntervals[
          target / tickIntervals[i - 1][2] < tickIntervals[i][2] / target
            ? i - 1
            : i
        ];
      step = i[1];
      interval = i[0];
    } else {
      step = Math.max(tickStep(start, stop, interval), 1);
      interval = millisecond;
    }
  }

  return step == null ? interval : interval.every(step);
}

function ticks(domain, interval, step) {
  var d = domain,
    t0 = d[0],
    t1 = d[d.length - 1],
    r = t1 < t0,
    t;
  // eslint-disable-next-line
  if (r) (t = t0), (t0 = t1), (t1 = t);
  t = tickInterval(interval, t0, t1, step);
  t = t ? t.range(t0, t1 + 1) : []; // inclusive stop
  return r ? t.reverse() : t;
}

// на этом паста заканчивается

const memoizedDiscontinuousScale = memoize(checkpoints =>
  discountinuousScale(checkpoints)
);

export function getScaleX(props: ScaleXProps): Scale {
  const dateToPlane = memoizedDiscontinuousScale(props.checkpoints);

  const planeToScreen = scaleLinear()
    .domain([
      dateToPlane(props.cursor.date) - props.timeSpan * props.cursor.ratio,
      dateToPlane(props.cursor.date) + props.timeSpan * (1 - props.cursor.ratio)
    ])
    .range([0, props.width]);

  const scaleX = date => planeToScreen(dateToPlane(date));

  scaleX.invert = point => dateToPlane.invert(planeToScreen.invert(point));

  scaleX.ticks = ticks.bind(null, [
    dateToPlane.invert(
      dateToPlane(props.cursor.date) - props.timeSpan * props.cursor.ratio
    ),
    dateToPlane.invert(
      dateToPlane(props.cursor.date) + props.timeSpan * (1 - props.cursor.ratio)
    )
  ]);

  return scaleX;
}

export function getScaleY(props: ScaleYProps): Scale {
  return scaleLinear()
    .domain(props.extent)
    .range([(props.modifier || 1) * props.height - 50, 50]);
}

type ScaleComponentProps = ScaleXProps &
  ScaleYProps & {
    animateTimeSpan: boolean,
    present: Types.RealtimePoint,
    children: ({
      scaleX: Scale,
      scaleY: Scale,
      present: Types.RealtimePoint
    }) => React.Element<*>
  };

const highPrecisionSpring = {
  stiffness: 170,
  damping: 26,
  precision: 0.00001
};

const lowPrecisionSpring = {
  stiffness: 170,
  damping: 26,
  precision: 1
};

export const AnimatedScales =  React.memo(function(props: ScaleComponentProps) {
  const style = {
    cursor_ratio: props.cursor.animate
      ? spring(props.cursor.ratio, highPrecisionSpring)
      : props.cursor.ratio,
    cursor_date: props.cursor.animate
      ? spring(props.cursor.date, lowPrecisionSpring)
      : props.cursor.date,
    presentDate: spring(props.present.date, lowPrecisionSpring),
    presentValue: spring(props.present.value, highPrecisionSpring),
    timeSpan: props.animateTimeSpan
      ? spring(props.timeSpan, lowPrecisionSpring)
      : props.timeSpan,
    extent_0: spring(props.extent[0], highPrecisionSpring),
    extent_1: spring(props.extent[1], highPrecisionSpring)
  };
  return (
    <Motion
      style={style}
      onRest={props.onRest}
    >
      {interpolated => {
        const present = {
          date: interpolated.presentDate,
          value: interpolated.presentValue,
          stock: props.present.stock
        };
        return props.children({
          scaleX: getScaleX({
            cursor: {
              ratio: interpolated.cursor_ratio,
              date: interpolated.cursor_date,
              follow: props.cursor.follow,
              animate: props.cursor.animate
            },

            timeSpan: interpolated.timeSpan,
            width: props.width,
            checkpoints: props.checkpoints
          }),
          scaleY: getScaleY({
            extent: [interpolated.extent_0, interpolated.extent_1],
            modifier: props.scaleModifier,
            height: props.height
          }),
          present: present
        });
      }}
    </Motion>
  );
});

export function NotAnimatedScales(props: ScaleComponentProps) {
  return props.children({
    scaleX: getScaleX(props),
    scaleY: getScaleY(props),
    present: props.present
  });
}



// WEBPACK FOOTER //
// ./src/components/Chart/Scales.js